<template>
  <div class="purchase">
    <v-loader v-if="isLoading" />
    <template v-else>
      <img class="icon" :src="purchase.product.icon_uri" alt="" aria-hidden />

      <h1 class="heading">{{ $jt(purchase.product.name_json) }}</h1>

      <p class="text">{{ $jt(purchase.product.description_json) }}</p>

      <p v-html="purchase.notice" class="text"></p>

      <p class="text text_margin-sm">
        <Icon icon="tabler:tags" class="text__icon" />
        <span>{{ purchase.product.type_translated }}</span>
      </p>

      <p v-if="purchase.renews_at" class="text text_margin-sm">
        <Icon icon="tabler:calendar-repeat" class="text__icon" />
        <span>{{ $t('SUBSCRIPTION_RENEWS_ON_DATE', { date: new Date(purchase.renews_at * 1000).toLocaleDateString() })
          }}</span>
      </p>

      <p v-if="isSubscription" class="text text_warning text_margin-sm">
        <Icon icon="tabler:exclamation-circle-filled" class="text__icon" />
        <html-with-links tag="span"
          :html="$t('SUBSCRIPTION_CAN_BE_CANCELED_ANYTIME', { subscriptionsLink: '/my/billing?subscriptions' })" />
      </p>

      <p v-if="isPerDevice" class="text text_warning text_margin-sm">
        <Icon icon="tabler:exclamation-circle-filled" class="text__icon" />
        <html-with-links tag="span"
          :html="$t('PURCHASE_WILL_APPLY_ONLY_TO_DEVICE', { device: purchase.device_model })" />
      </p>

      <p class="text text_margin-sm">
        {{ $t('PRICE') }}: <b>{{ formatCurrency(purchase.product.price_cents_eur) }}</b>
      </p>
      <p v-if="purchase.status === 'new'" class="text text_margin-sm">
        {{ $t('PURCHASE_DUE_NOW') }}: <b>{{ formatCurrency(purchase.due_cents_eur) }}</b>
      </p>

      <p v-if="purchase.status === 'ok'" class="text text_success text_margin-sm">
        <Icon icon="tabler:circle-check-filled" class="text__icon" />
        <span>{{ $t('PURCHASE_STATUS_OK_MESSAGE') }}</span>
      </p>

      <v-checkbox v-if="mustAcceptAgreement" v-model="isAgreementAccepted" class="agreement">
        <i18next path="PURCHASE_AGREE_WRAPPER" tag="span">
          <button class="g-pseudo-link" place="tos" @click="showTos">{{ $t('PURCHASE_AGREE_TOS_PART') }}</button>
        </i18next>
      </v-checkbox>
      <div v-else-if="purchase.product.tos_text" class="text text_margin-sm">
        <button @click="showTos" class="g-pseudo-link">{{ $t('TERMS OF SERVICE') }}</button>
      </div>


      <div class="controls">
        <v-button
          :tag="purchase.redirect_uri ? 'a' : 'button'"
          :href="purchase.redirect_uri"
          type="primary"
          size="lg"
          @click.native="onSubmitButtonClick"
        >
          {{ submitButtonLabel }}
        </v-button>
        <v-button v-if="purchase.next_action !== 'return_to_app'" tag="a" :href="cancelUrl" type="negative" size="lg">
          {{ $t('CANCEL') }}
        </v-button>
      </div>

      <v-modal name="tos" :width="750" height="auto" scrollable close-button>
        <v-markdown class="tos-text" :content="purchase.product.tos_text"/>
      </v-modal>
    </template>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue2';
import VButton from '@/components/Button';
import HtmlWithLinks from '@/components/HtmlWithLinks';
import VLoader from '@/components/Loader';
import { APIError } from '@/plugins/Api';
import formatCurrency from '@/utils/formatCurrency';
import VCheckbox from '@/components/Checkbox';
import VModal from '@/components/VModal.vue';
import VMarkdown from '@/components/VMarkdown.vue';

export default {
  components: {
    Icon,
    VButton,
    HtmlWithLinks,
    VLoader,
    VCheckbox,
    VModal,
    VMarkdown,
  },
  data() {
    return {
      isLoading: false,
      isProcessing: false,
      isAgreementAccepted: false,
      purchase: {
        product: {
          identifier: '',
          name_json: '',
          description_json: '',
          type: '',
          type_translated: '',
          price_cents_eur: undefined,
          app_identifier: '',
          amount: undefined,
          icon_uri: '',
          tos_text: '',
          license_type: '',
        },
        redirect_uri: '',
        next_action: '',
        renews_at: undefined,
        device_model: '',
        status: '',
        due_cents_eur: undefined,
        notice: '',
      },
    };
  },
  computed: {
    title() {
      return this.$jt(this.purchase.product.name_json);
    },
    purchaseTicket() {
      return this.$route.query.purchaseTicket;
    },
    appIdentifier() {
      return this.$route.query.appIdentifier;
    },
    isAppProduct() {
      return this.purchase.product.identifier.startsWith('app');
    },
    isSubscription() {
      return this.purchase.product.type.includes('subscription');
    },
    isPerDevice() {
      return this.purchase.product.license_type === 'per_device';
    },
    mustAcceptAgreement() {
      return this.purchase.product.tos_text && this.purchase.status === 'new';
    },
    submitButtonLabel() {
      switch (this.purchase.next_action) {
        case 'confirm':
          return this.$t('CONFIRM_PURCHASE');
        case 'redirect':
          return this.$t('PROCEED_TO_PAYMENT');
        case 'return_to_app':
        default:
          return this.$t('RETURN_TO_APP');
      }
    },
    cancelUrl() {
      if (this.isAppProduct) {
        return `https://${this.BRAND_CONFIG.domain}/details/${this.appIdentifier}`;
      }

      return `appdb.${this.appIdentifier}://verifyPurchase=cancelled`;
    },
  },
  methods: {
    formatCurrency,
    async loadPurchase() {
      this.isLoading = true;

      try {
        const purchase = await this.$api.processPurchase({
          purchaseTicket: this.purchaseTicket,
          appIdentifier: this.appIdentifier,
        });

        this.purchase = purchase;
      } catch (e) {
        this.$router.replace({
          name: 'error',
          params: {
            message: e instanceof APIError ? e.translatedMessage : e.message,
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
    async onSubmitButtonClick(event) {
      if (this.mustAcceptAgreement && !this.isAgreementAccepted) {
        event.preventDefault();
        this.$notify({
          type: 'error',
          text: this.$t('PURCHASE_AGREEMENT_REQUIRED'),
          position: 'top left',
        });
        return;
      }

      if (this.purchase.redirect_uri) {
        return;
      }

      this.isProcessing = true;
      try {
        this.purchase = await this.$api.processPurchase({
          operation: this.purchase.next_action,
          purchaseTicket: this.purchaseTicket,
          appIdentifier: this.appIdentifier,
        });
      } finally {
        this.isProcessing = false;
      }
    },
    showTos() {
      this.$modal.show('tos');
    },
  },
  created() {
    this.loadPurchase();
  },
};
</script>

<style lang="scss" scoped>
.purchase {
  text-align: center;
}

.icon {
  @include icon-shape;
  width: 7rem;
  height: 7rem;
}

.heading {
  font-size: 1.4rem;
  font-weight: 500;
  margin: 1rem 0;
}

.text {
  line-height: 1.2;
  margin: 1rem 0;

  &_margin-sm {
    margin: 0.5rem 0;
  }

  &_warning {
    .text__icon {
      color: var(--text-warning);
    }
  }

  &_success {
    .text__icon {
      color: var(--text-success);
    }
  }

  &__icon {
    width: 1.3em;
    height: 1.3em;
    vertical-align: bottom;
    position: relative;
    bottom: -0.05em;
    margin-right: 0.3em;
  }

  &::v-deep {
    b {
      font-weight: 500;
    }
  }
}

.agreement {
  margin: 0.75rem 0 1rem 0;
  justify-content: center;
  text-align: left;

  &__button {
    color: var(--primary);
    text-decoration: underline;
  }
}

.controls {
  display: grid;
  grid-template-columns: max-content;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem auto 0;
}

.tos-text {
  text-align: left;
}

button {
  font-size: inherit;
}
</style>
